import React, { useEffect, useState } from "react"
import {
	default as ReactSelect,
	components,
	OptionProps,
	ControlProps,
	SingleValue,
	MultiValue,
	ActionMeta,
	PlaceholderProps,
} from "react-select"
import { useIntl } from "react-intl"
import { Search } from "@walmart-web/livingdesign-icons"
import { Tuple } from "components/Shared/Interfaces"
import {
	optionsSelected,
	renderPlaceholder,
	EMPTY_STRING,
	getSymetricDifferenceArray,
} from "./utils"
import { Loading, Title } from "./components"
import "./index.scss"
import { addAllOption } from "components/Shared/GlobalFilter/utils"
import { capitalizeTuple } from "../utils"
import { useFiltersContext } from "components/Shared/GlobalFilter/context/filtersContext"
import { optionsSelected as optSelected } from "components/Shared/Filters/utils"
import withVisibility from "components/Shared/Hocs/withVisibility/withVisibility"
import { FilterOptionOption } from "react-select/dist/declarations/src/filters"

export interface SelectProps {
	id: string
	options: Array<Tuple>
	isLoading?: boolean
	isDisabled?: boolean
	isMulti?: boolean
	isClearable?: boolean
	disabledText?: string
	onChange: (value: Array<Tuple>, action?: ActionMeta<Tuple>) => void
	onBlur?: (value: Array<Tuple>) => void
	initialValue?: boolean
	allOption?: boolean
	codeLabels?: boolean
	selected?: Array<Tuple>
	hasTitle?: boolean
}

const Control = ({ children, ...props }: ControlProps<Tuple>) => (
	<components.Control {...props}>
		<Search className="filters-select-control" /> {children}
	</components.Control>
)

const ValidateLabel = (codeLabels: boolean, code: string) => {
	return codeLabels && code !== "0"
}

const Option = (
	props: OptionProps<Tuple>,
	isMulti: boolean,
	codeLabels: boolean
) => {
	const intl = useIntl()
	return (
		<components.Option className="filters-select-option" {...props}>
			{isMulti && (
				<input type="checkbox" checked={props.isSelected} onChange={() => {}} />
			)}
			{ValidateLabel(codeLabels, props.data.code) ? (
				<span className={"option-main"} data-testid="option-main">
					<b>{props.label.substring(props.label.indexOf(" ") + 1)}</b>
					<p>
						<span>
							{intl.formatMessage({ id: "phronesis.filters.products.number" })}:
						</span>
						{props.data.code}
					</p>
					<p>
						<span>
							{intl.formatMessage({ id: "phronesis.filters.products.upc" })}:
						</span>
						{props.label.substring(0, props.label.indexOf(" "))}
					</p>
				</span>
			) : (
				<span className={"option-main"} data-testid="option-main">
					{props.label}
				</span>
			)}
		</components.Option>
	)
}

const Select: React.FC<SelectProps> = ({
	id,
	options,
	isLoading,
	isDisabled,
	isMulti,
	isClearable = true,
	allOption = false,
	initialValue = false,
	codeLabels = false,
	onChange,
	onBlur = () => {},
	selected,
	hasTitle = true,
}) => {
	const { filters = {}, setFilters } = useFiltersContext()
	const globalFilterOptionsSelected = [...(filters[id] ?? [])]
	const capitalizedOptions = capitalizeTuple(options)
	const optionsWithAll = addAllOption(capitalizedOptions)
	const allOptions = allOption ? optionsWithAll : capitalizedOptions
	const [selectedValues, setSelectedValues] = useState<Array<Tuple>>(
		globalFilterOptionsSelected
	)
	const [input, setInput] = useState<string>(EMPTY_STRING)
	const intl = useIntl()
	const title = `phronesis.filters.${id}`

	const getTitle = () => {
		return codeLabels ? title + "_multiple" : title
	}

	const place = (
		{ children, ...props }: PlaceholderProps<Tuple>,
		codeLabels: boolean
	) => {
		return (
			<components.Placeholder {...props}>
				{codeLabels && props.getValue().length > 0
					? props
							.getValue()[0]
							.name.substring(props.getValue()[0].name.indexOf(" ") + 1)
					: children}
			</components.Placeholder>
		)
	}

	const defaultPlaceHolder = intl.formatMessage(
		{ id: "phronesis.filter" },
		{ value: intl.formatMessage({ id: title }).toLowerCase() }
	)

	useEffect(() => {
		if (selected) {
			setSelectedValues(selected)
		}
	}, [selected])

	useEffect(() => {
		if (options.length && initialValue) {
			const option = [capitalizedOptions[0]]
			setFilters({ ...filters, [id]: option })
			setSelectedValues(option)
			onChange(option, undefined)
		}
	}, [isLoading])

	useEffect(() => {
		const optionsWithDifferences = getSymetricDifferenceArray(
			globalFilterOptionsSelected,
			selectedValues
		)
		const hasOptionsDifferences = optionsWithDifferences.length > 0
		if (!hasOptionsDifferences) {
			return
		}

		let optionsGlobalFilterToSync: Tuple[] = []
		if (globalFilterOptionsSelected.length > 0) {
			optionsGlobalFilterToSync = optSelected(
				globalFilterOptionsSelected[0],
				options,
				getOptionsFiltered(),
				allOption
			)
		}
		setSelectedValues(optionsGlobalFilterToSync)
	}, [JSON.stringify(globalFilterOptionsSelected)])

	const getOptionsFiltered = () => {
		return allOptions.filter((o) => validateIncludes(o.name, o.code, input))
	}
	const selectOnChange = (
		value: SingleValue<Tuple> | MultiValue<Tuple>,
		action: ActionMeta<Tuple>
	) => {
		const selection = Array.isArray(value)
			? value
			: value
			? ([value] as Tuple[])
			: []
		const opt = optionsSelected(
			selection,
			selectedValues,
			getOptionsFiltered(),
			allOption,
			isMulti
		)
		setSelectedValues(opt)
		onChange(opt, action)
	}

	const validateFilterAll = (option: FilterOptionOption<Tuple>) => {
		return option.value === "0" && getOptionsFiltered().length > 1
	}

	const validateIncludes = (name: string, code: string, searchText: string) => {
		return (
			name.toLowerCase().includes(searchText.toLowerCase()) ||
			code.includes(searchText.toLowerCase())
		)
	}

	const filterOptions = (
		option: FilterOptionOption<Tuple>,
		searchText: string
	) => {
		return (
			(validateIncludes(option.label, option.value, searchText) &&
				option.value != "0") ||
			validateFilterAll(option)
		)
	}

	const renderSelect = () => {
		if (isLoading) {
			return <Loading intl={intl} title={getTitle()} />
		}
		return (
			<ReactSelect
				id={getTitle()}
				className="filters-select-select"
				classNamePrefix="filters-select"
				value={selectedValues}
				options={allOptions}
				isDisabled={isDisabled}
				inputValue={input}
				filterOption={filterOptions}
				onInputChange={(value, action) => {
					switch (action.action) {
						case "input-change":
							setInput(value)
							return
						case "menu-close":
							setInput(EMPTY_STRING)
							return
					}
				}}
				placeholder={renderPlaceholder(defaultPlaceHolder, selectedValues)}
				isMulti={isMulti}
				components={{
					Option: (props) => Option(props, !!isMulti, codeLabels),
					Control,
					Placeholder: (props) => place(props, codeLabels),
				}}
				getOptionLabel={(options) => options.name}
				getOptionValue={(options) => options.code}
				controlShouldRenderValue={!isMulti}
				closeMenuOnSelect={!isMulti}
				hideSelectedOptions={false}
				onBlur={() => {
					onBlur(selectedValues)
				}}
				onChange={(value, action) => {
					switch (action.action) {
						case "clear":
							onChange([], action)
							setSelectedValues([])
							return
						default:
							selectOnChange(value, action)
							return
					}
				}}
				noOptionsMessage={() =>
					intl.formatMessage({ id: "phronesis.notfound" })
				}
				isClearable={isClearable}
			/>
		)
	}

	const SelectTitle: React.FC = () => {
		return hasTitle ? (
			<Title
				title={intl.formatMessage({ id: getTitle() })}
				isDisabled={isDisabled}
				capitalizeFirst={codeLabels}
			/>
		) : (
			<React.Fragment />
		)
	}

	return (
		<div className="filters-select">
			<SelectTitle />
			{renderSelect()}
		</div>
	)
}

export default withVisibility(Select)
